import { useTranslation } from 'react-i18next';
import {
    Info,
    AlertTriangle,
    XCircle,
    CheckCircle2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { NotificationEventActionBadge } from '@/components/notifications/NotificationEventActionBadge.jsx';
import { localizedNotificationEventActionLabel } from '@/lib/notification-event-presentation.js';
import {
    buildNotificationInboxCopy,
    formatNotificationAbsoluteTime,
    formatNotificationRelativeTime,
    isSystemNotificationActor,
    resolveNotificationActorDisplayName,
    resolveNotificationEventName,
} from '@/lib/notifications/notification-inbox-row.js';

function getNotificationIcon(type) {
    const iconClass = 'h-4 w-4 shrink-0';
    const normalized = String(type || '').toLowerCase();
    switch (normalized) {
        case 'success':
            return <CheckCircle2 className={cn(iconClass, 'text-success')} />;
        case 'warning':
            return <AlertTriangle className={cn(iconClass, 'text-warning')} />;
        case 'error':
        case 'alert':
            return <XCircle className={cn(iconClass, 'text-destructive')} />;
        default:
            return <Info className={cn(iconClass, 'text-info')} />;
    }
}

/**
 * Shared inbox row for My Work Bildirimler and the header bell.
 * `density="compact"` keeps the 420px dropdown readable.
 */
export function NotificationInboxRow({
    row,
    title,
    message,
    language,
    moduleLabel = '',
    density = 'comfortable',
    showTime = false,
    className,
}) {
    const { t } = useTranslation('notificationInbox');
    const actorDisplayName = resolveNotificationActorDisplayName(row);
    const actorLabelName = isSystemNotificationActor(row)
        ? t('notifications.systemActor', { defaultValue: 'System' })
        : actorDisplayName;
    const compact = density === 'compact';
    const eventName = resolveNotificationEventName(row);
    const actionLabel = eventName
        ? localizedNotificationEventActionLabel(eventName, language)
        : '';
    const copy = buildNotificationInboxCopy(row, { title, message });
    const absolute = showTime
        ? formatNotificationAbsoluteTime(row.timestamp, language)
        : '';

    return (
        <div className={cn('flex min-w-0 items-start gap-3', compact && 'gap-2.5', className)}>
            <div className="pt-1.5" aria-hidden>
                {!row.isRead ? (
                    <div className="h-2 w-2 rounded-full bg-primary" />
                ) : (
                    <div className="h-2 w-2" />
                )}
            </div>
            <div className="pt-0.5">{getNotificationIcon(row.type)}</div>
            <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 flex-wrap items-start gap-2">
                        <span
                            className={cn(
                                'text-sm leading-snug',
                                !row.isRead && 'font-semibold text-foreground',
                                row.isRead && 'font-medium text-foreground/80',
                            )}
                        >
                            {copy.primary}
                        </span>
                        {eventName ? (
                            <NotificationEventActionBadge
                                eventName={eventName}
                                label={actionLabel}
                                className="mt-0.5"
                            />
                        ) : null}
                        {moduleLabel ? (
                            <span className="mt-0.5 rounded-[var(--ds-radius-control-compact)] bg-muted px-[var(--ds-space-1-5)] py-[var(--ds-space-half)] text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                                {moduleLabel}
                            </span>
                        ) : null}
                    </div>
                    {showTime ? (
                        <span
                            className="shrink-0 whitespace-nowrap text-[10px] font-medium text-muted-foreground"
                            title={absolute}
                        >
                            {formatNotificationRelativeTime(row.timestamp, language)}
                        </span>
                    ) : null}
                </div>
                {actorLabelName ? (
                    <p
                        className={cn(
                            'max-w-full text-muted-foreground [overflow-wrap:anywhere]',
                            compact ? 'text-[11px]' : 'text-xs',
                        )}
                    >
                        {t('notifications.actorLabel', {
                            name: actorLabelName,
                            defaultValue: 'Performed by: {{name}}',
                        })}
                    </p>
                ) : null}
                {copy.subtitle ? (
                    <p className={cn(
                        'text-muted-foreground',
                        compact ? 'line-clamp-1 text-[11px]' : 'text-xs',
                    )}
                    >
                        {copy.subtitle}
                    </p>
                ) : null}
                {copy.body ? (
                    <p className={cn(
                        'leading-relaxed text-muted-foreground',
                        compact ? 'line-clamp-2 text-xs' : 'line-clamp-2 text-sm',
                    )}
                    >
                        {copy.body}
                    </p>
                ) : null}
            </div>
        </div>
    );
}
